/**
 * ユーザー画面で共有する小さな部品: カスタム属性の入力欄、パスワード再設定 Modal、削除 Modal。
 * (平文の 1 回表示と初期パスワードの通知は `userPassword.tsx`)
 */
import { useMutation } from "@tanstack/react-query";
import { Flex, Form, Input, Modal, Radio, Space, Switch, Typography, message } from "antd";

import { tellPassword } from "./userPassword";
import { useEffect, useState } from "react";

import { useApi } from "../../lib/useApi";
import { errorText, useIdpCustomAttrs, useInvalidateIdp } from "./hooks";

const { Text, Paragraph } = Typography;

/** 追加・編集フォームの値。 */
export type UserValues = {
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  enabled?: boolean;
  roles?: string[];
  attrs?: Record<string, string>;
  generate?: boolean;
  password?: string;
  temporary?: boolean;
};

/** カスタム属性の入力欄(宣言済みのものだけ出す)。 */
export function AttrFields({ disabled = false }: { disabled?: boolean }) {
  const attrs = useIdpCustomAttrs();
  const items = attrs.data?.items ?? [];
  if (items.length === 0) return null;
  return (
    <>
      {items.map((a) => (
        <Form.Item key={a.name} name={["attrs", a.name]} label={a.displayName || a.name}>
          <Input maxLength={255} disabled={disabled} />
        </Form.Item>
      ))}
    </>
  );
}

// ---- パスワードの再設定 -------------------------------------------------------------

export function ResetPasswordModal({
  id,
  username,
  open,
  onClose,
}: {
  id: string;
  username: string;
  open: boolean;
  onClose: () => void;
}) {
  const api = useApi();
  const invalidate = useInvalidateIdp();
  const [generate, setGenerate] = useState(true);
  const [password, setPassword] = useState("");
  const [temporary, setTemporary] = useState(true);

  useEffect(() => {
    if (open) {
      setGenerate(true);
      setPassword("");
      setTemporary(true);
    }
  }, [open]);

  const reset = useMutation({
    mutationFn: () => api.resetIdpPassword(id, { generate, password, temporary }),
    onSuccess: (res) => {
      invalidate();
      tellPassword(res);
      onClose();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  return (
    <Modal
      open={open}
      title={`${username} のパスワードを再設定`}
      onCancel={onClose}
      onOk={() => reset.mutate()}
      okText="再設定する"
      cancelText="やめる"
      confirmLoading={reset.isPending}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: "100%" }}>
        <Radio.Group value={generate} onChange={(e) => setGenerate(Boolean(e.target.value))}>
          <Radio value={true}>自動で作る(推奨)</Radio>
          <Radio value={false}>自分で決める</Radio>
        </Radio.Group>
        {!generate && (
          <Input.Password
            value={password}
            maxLength={200}
            placeholder="新しいパスワード"
            onChange={(e) => setPassword(e.target.value)}
          />
        )}
        <Flex gap={8} align="center">
          <Switch checked={temporary} onChange={setTemporary} />
          <Text>次のログインで変更させる</Text>
        </Flex>
        <Text type="secondary">
          メールが送れるときは本人にメールで送り、画面には出さない。
        </Text>
      </Space>
    </Modal>
  );
}

// ---- 削除 ---------------------------------------------------------------------------

/** 削除。**ユーザー名を打ち直させる**(取り消せないので)。 */
export function DeleteUserModal({
  id,
  username,
  open,
  onClose,
  onDone,
}: {
  id: string;
  username: string;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const api = useApi();
  const invalidate = useInvalidateIdp();
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (open) setTyped("");
  }, [open]);

  const remove = useMutation({
    mutationFn: () => api.deleteIdpUser(id),
    onSuccess: (res) => {
      invalidate();
      void message.success(`${res.username} を削除した`);
      onClose();
      onDone();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  return (
    <Modal
      open={open}
      title={`${username} を削除する`}
      onCancel={onClose}
      okText="削除する"
      okButtonProps={{ danger: true, disabled: typed !== username }}
      cancelText="やめる"
      confirmLoading={remove.isPending}
      onOk={() => remove.mutate()}
      destroyOnClose
    >
      <Paragraph>
        Keycloak からも消える(戻せない)。確認のため <Text code>{username}</Text> と打つ。
      </Paragraph>
      <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={username} />
    </Modal>
  );
}
